import { orderedFreeWorkoutIds, readFreeWorkoutOrder, saveFreeWorkoutOrder } from "@/lib/fitness/freeWorkoutBuilder";
import { localizeFitnessProductExerciseName } from '@/lib/fitnessProductExerciseLabels';
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { normalizeFreeWorkoutGroups, readFreeWorkoutGroups, getFreeWorkoutExerciseChoices, normalizeFreeWorkoutSelections, readFreeWorkoutSelections, saveFreeWorkoutSelections, type FreeWorkoutSelections } from "@/lib/fitness/freeWorkoutSelection";
import { motion, useReducedMotion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { ChevronLeft, Dumbbell } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { MUSCLE_GROUP_MAP } from "@/lib/fitnessMuscleGroups";
import { MUSCLE_GROUP_LIST, MuscleGroupKey } from "@/lib/exerciseLibrary";

const PARTICLE_CONFIGS = [
  { w: 2, color: "#00e5ff", opacity: 0.17, left: "12%", top: "18%", yDrift: -15, xDrift: 6, dur: 10, delay: 0 },
  { w: 1.5, color: "hsl(265 60% 65%)", opacity: 0.15, left: "78%", top: "30%", yDrift: -12, xDrift: -5, dur: 12, delay: 1.5 },
  { w: 2.5, color: "#00e5ff", opacity: 0.18, left: "45%", top: "55%", yDrift: -18, xDrift: 4, dur: 9, delay: 0.8 },
  { w: 1.8, color: "hsl(265 60% 65%)", opacity: 0.16, left: "28%", top: "72%", yDrift: -14, xDrift: -7, dur: 11, delay: 2.5 },
  { w: 2, color: "#00e5ff", opacity: 0.19, left: "65%", top: "42%", yDrift: -16, xDrift: 5, dur: 13, delay: 1 },
  { w: 1.6, color: "hsl(265 60% 65%)", opacity: 0.15, left: "88%", top: "65%", yDrift: -13, xDrift: -4, dur: 11.5, delay: 2 },
];

const FitnessParticles = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
    {PARTICLE_CONFIGS.map((p, i) => (
      <motion.div key={i} className="absolute rounded-full"
        style={{ width: p.w, height: p.w, background: p.color, opacity: p.opacity, left: p.left, top: p.top }}
        animate={{ y: [0, p.yDrift, 0], x: [0, p.xDrift, 0], opacity: [p.opacity, p.opacity + 0.04, p.opacity] }}
        transition={{ duration: p.dur, repeat: Infinity, ease: "easeInOut", delay: p.delay }} />
    ))}
  </div>
));
FitnessParticles.displayName = "FitnessParticles";

const FitnessBreathingOrbs = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
    <motion.div
      className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(0,229,255,0.04) 0%, transparent 70%)" }}
      animate={{ opacity: [0.3, 0.6, 0.3], scale: [0.95, 1.05, 0.95] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute -bottom-32 -left-24 w-[250px] h-[250px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(139,92,246,0.03) 0%, transparent 70%)" }}
      animate={{ opacity: [0.2, 0.5, 0.2], scale: [1, 1.08, 1] }}
      transition={{ duration: 10, delay: 2, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
));
FitnessBreathingOrbs.displayName = "FitnessBreathingOrbs";

const GROUP_LABEL_KEY: Record<MuscleGroupKey, string> = {
  chest: MUSCLE_GROUP_MAP.chest,
  back: MUSCLE_GROUP_MAP.back,
  shoulders: MUSCLE_GROUP_MAP.shoulders,
  biceps: "fitness.muscles.biceps",
  triceps: "fitness.muscles.triceps",
  legs: MUSCLE_GROUP_MAP.legs,
  core: MUSCLE_GROUP_MAP.core,
  glutes: MUSCLE_GROUP_MAP.glutes,
  cardio: MUSCLE_GROUP_MAP.cardio,
};

interface SelectedExercise {
  name: string;
  muscle: MuscleGroupKey;
  defaultWeight: number;
  defaultReps: number;
  sets: number;
}

const FreeWorkoutExerciseSelect: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, tf, lang } = useLanguage();
  const reducedMotion = useReducedMotion();

  const selectedGroups = useMemo<MuscleGroupKey[]>(() => {
    const state: unknown = location.state;
    if (state && typeof state === "object" && "selectedGroups" in state) {
      return normalizeFreeWorkoutGroups(state.selectedGroups);
    }
    return readFreeWorkoutGroups();
  }, [location.state]);

  const [selections, setSelections] = useState<FreeWorkoutSelections>(() => readFreeWorkoutSelections(selectedGroups));
  const starting = useRef(false);
  const [selectionOrder, setSelectionOrder] = useState<unknown>(readFreeWorkoutOrder);
  const visibleSelections = useMemo(() => normalizeFreeWorkoutSelections(selections, selectedGroups), [selections, selectedGroups]);

  useEffect(() => {
    // A same-route update must not keep picks from groups that are no longer shown.
    starting.current = false;
    setSelections(previous => normalizeFreeWorkoutSelections(previous, selectedGroups));
    if (selectedGroups.length === 0) {
      navigate("/fitness/free-workout", { replace: true });
    }
  }, [selectedGroups, navigate]);

  useEffect(() => {
    if (!starting.current) saveFreeWorkoutSelections(visibleSelections, selectedGroups);
  }, [visibleSelections, selectedGroups]);

  const togglePick = (group: MuscleGroupKey, exerciseId: string) => {
    setSelectionOrder(previous => { const ids = Array.isArray(previous) ? previous : []; return visibleSelections[group]?.includes(exerciseId) ? ids.filter(id => id !== exerciseId) : [...ids, exerciseId]; });
    setSelections(prev => {
      const current = prev[group] ?? [];
      const next = current.includes(exerciseId)
        ? current.filter(id => id !== exerciseId)
        : [...current, exerciseId];
      return { ...prev, [group]: next };
    });
  };

  const orderedIds = useMemo(() => orderedFreeWorkoutIds(selectionOrder, visibleSelections, selectedGroups), [selectionOrder, visibleSelections, selectedGroups]);
  useEffect(() => { saveFreeWorkoutOrder(orderedIds); }, [orderedIds]);
  const numbering = useMemo(() => new Map(orderedIds.map((id, index) => [id, index + 1])), [orderedIds]);

  const totalSelected = numbering.size;

  const handleStart = () => {
    if (totalSelected === 0 || starting.current) return;
    const exercises: SelectedExercise[] = [];
    for (const group of MUSCLE_GROUP_LIST) {
      if (!selectedGroups.includes(group)) continue;
      const picked = visibleSelections[group];
      if (!picked || picked.length === 0) continue;
      const pickedSet = new Set(picked);
      for (const ex of getFreeWorkoutExerciseChoices(group)) {
        if (pickedSet.has(ex.id)) {
          exercises.push({
            name: ex.i18nKey,
            muscle: group,
            defaultWeight: 0,
            defaultReps: 10,
            sets: 3,
          });
        }
      }
    }
    const byName = new Map(exercises.map(exercise => [exercise.name, exercise]));
    const ordered = orderedIds.flatMap(id => { const choice = selectedGroups.flatMap(getFreeWorkoutExerciseChoices).find(exercise => exercise.id === id); const exercise = choice && byName.get(choice.i18nKey); return exercise ? [exercise] : []; });
    starting.current = true;
    navigate("/fitness/free-workout/review", { state: { exercises: ordered, source: "selection" } });
  };

  const subtitle = totalSelected > 0
    ? tf("fitness.freeWorkout.selectedCount", { count: String(totalSelected) })
    : t("fitness.freeWorkout.exerciseSelectSubtitle");

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      {!reducedMotion && <><FitnessParticles /><FitnessBreathingOrbs /></>}

      <button onClick={() => navigate("/fitness/free-workout")} className="flex items-center gap-1 text-muted-foreground mb-4 active:opacity-70 relative z-10">
        <ChevronLeft size={16} />
        <span className="text-[13px] font-display">{t("common.back")}</span>
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-6 relative z-10">
        <div>
          <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
            {t("fitness.freeWorkout.title").toUpperCase()}
          </p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("fitness.freeWorkout.title")}</h1>
          <p className="text-[12px] font-body text-foreground/55 mt-2">{subtitle}</p>
        </div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}>
          <Dumbbell size={16} style={{ color: "hsl(187 72% 50%)" }} />
        </div>
      </motion.div>

      <div className="relative z-10 space-y-6 pb-16">
        {selectedGroups.map((group, gIdx) => {
          const exercises = getFreeWorkoutExerciseChoices(group);
          return (
            <motion.section
              key={group}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 + gIdx * 0.05, duration: 0.4 }}
            >
              <p
                className="font-display text-[14px] uppercase tracking-[0.2em] mb-3"
                style={{ color: "hsl(187 72% 50% / 0.55)" }}
              >
                {t(GROUP_LABEL_KEY[group])}
              </p>
              <div className="flex flex-col gap-2">
                {exercises.map(ex => {
                  const badgeNumber = numbering.get(ex.id);
                  const isSelected = badgeNumber !== undefined;
                  return (
                    <button
                      key={ex.id}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => togglePick(group, ex.id)}
                      className="flex items-center justify-between px-4 py-3 rounded-xl active:scale-[0.98] transition-all"
                      style={{
                        minHeight: 52,
                        background: isSelected ? "hsl(187 72% 50% / 0.10)" : "hsl(var(--card))",
                        border: isSelected ? "1.5px solid hsl(187 72% 50%)" : "1px solid hsl(222 18% 22%)",
                      }}
                    >
                      <span
                        className="font-body text-[13px] text-left"
                        style={{ color: isSelected ? "hsl(187 72% 70%)" : "hsl(210 20% 90%)" }}
                      >
                        {localizeFitnessProductExerciseName(ex.i18nKey, lang) ?? t(ex.i18nKey)}
                      </span>
                      {isSelected && (
                        <span
                          className="flex items-center justify-center shrink-0 ml-2 rounded-full font-display text-[13px] font-bold"
                          style={{
                            width: 28,
                            height: 28,
                            background: "hsl(187 72% 50%)",
                            color: "hsl(var(--background))",
                            boxShadow: "0 0 10px hsl(187 72% 50% / 0.5)",
                          }}
                        >
                          {badgeNumber}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.section>
          );
        })}
      </div>

      {/* Fixed start button */}
      <div className="fixed bottom-20 left-0 right-0 px-6 z-20 pointer-events-none">
        <div className="max-w-[500px] mx-auto pointer-events-auto">
          <button
            onClick={handleStart}
            disabled={totalSelected === 0}
            className="w-full py-4 rounded-xl font-display text-[14px] font-semibold tracking-wide active:scale-[0.97] transition-all"
            style={{
              background: totalSelected === 0 ? "hsl(222 18% 14%)" : "hsl(var(--primary))",
              color: totalSelected === 0 ? "hsl(218 14% 45%)" : "hsl(var(--background))",
              opacity: totalSelected === 0 ? 0.55 : 1,
              boxShadow: totalSelected === 0 ? "none" : "0 0 22px hsl(187 72% 50% / 0.28)",
            }}
          >
            {t("fitness.freeWorkout.startBtn")}
          </button>
        </div>
      </div>

    </MobileLayout>
  );
};

export default FreeWorkoutExerciseSelect;
